<script setup>
import { useRoute } from 'vue-router'

const props = defineProps({
    destination: {
        type: Object,
        required: true,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    premium: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['click', 'retap'])

const route = useRoute()

// A tap on the lit tab is a RE-TAP only at the tab's own root (iOS: a lit tab deep in its
// stack goes to its root first; tapping again there scrolls to top). Search re-taps on
// its landing AND its results pages — both focus the field.
function isRetap() {
    if (!props.selected) return false
    return props.destination.id === 'search' || route.name === props.destination.to.name
}

// Bound in the CAPTURE phase (see nav-item.vue): RouterLink's own `navigate` is a
// bubble-phase listener on the same <a>, so this runs first and its preventDefault —
// ours for a re-tap, the bar's for the logged-out Library gate — is seen by guardEvent.
function onClick(event) {
    // Modifier clicks belong to the browser (new tab/window) — never swallow them.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (isRetap()) {
        event.preventDefault()
        emit('retap')
        return
    }
    emit('click', event)
}
</script>

<template>
    <router-link
        :to="destination.to"
        :class="['tab-item', { 'tab-item-selected': selected, 'tab-item-premium': premium }]"
        :aria-current="selected ? 'page' : undefined"
        @click.capture="onClick"
    >
        <BaseIcon
            class="tab-item-icon"
            :name="destination.icon"
            :filled="selected && !premium"
            :size="24"
        />
        <span class="tab-item-label">{{ destination.label }}</span>
    </router-link>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// Figma Tab/Item 529:1116: 24 glyph at 1.25 over Caption/Regular, gap 4, padding 8/4/4.
$nav-stroke: 1.25;
$premium-stroke: 1.5;

.tab-item {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $space-4;
    align-items: center;
    justify-content: center;
    min-width: 0;
    padding: $space-8 $space-4 $space-4;
    text-decoration: none;
    border-radius: $radius-12;

    @include transition((color, background-color), fast);
    @include focusRing;

    &,
    &:visited {
        color: $c-text-tertiary;
    }

    // Pressed = state/pressed wash on the inset pill.
    &:active {
        background: $c-state-pressed;
    }

    // Hover is a pointer affordance; touch devices get pressed only.
    @media (hover: hover) {
        &:hover,
        &:visited:hover {
            color: $c-text-secondary;
            text-decoration: none;
        }

        // The hover rule outranks .tab-item-selected, so the selected tab re-asserts
        // text/primary or hovering it would dim the active tab.
        &.tab-item-selected:hover,
        &.tab-item-selected:visited:hover {
            color: $c-text-primary;
        }
    }
}

// Selected = filled twin + text/primary, no chip (iOS twin).
.tab-item-selected,
.tab-item-selected:visited {
    color: $c-text-primary;
}

.tab-item-icon {
    flex-shrink: 0;
    stroke-width: $nav-stroke;
}

.tab-item-label {
    @include captionRegular;

    color: inherit;
    // Labels never wrap or truncate: a larger browser font grows the bar in height, not
    // the label onto two lines (overflow at very large text on 320px is a device-pass check).
    white-space: nowrap;
}

// Premium: accent mark always; the label turns accent only when selected.
.tab-item-premium {
    .tab-item-icon {
        color: $c-accent-default;
        stroke-width: $premium-stroke;
    }

    &.tab-item-selected .tab-item-label {
        color: $c-accent-default;
    }
}
</style>
